import React from 'react';

/**
 * Tracks the HTML editor iframe preview lifecycle.
 *
 * After a left-panel form save, `regeneratePreview` shows a spinner (create
 * flow) and bumps a cache-bust version so the iframe reloads once the backend
 * has persisted. Inline body edits call `bumpPreviewVersion` directly.
 */
export function useHtmlEditorPreview() {
  const [renderingPreview, setRenderingPreview] = React.useState(false);
  const [renderPreviewVersion, setRenderPreviewVersion] = React.useState(0);
  // A timer left running past unmount sets state on a torn-down tree; under jsdom it fires
  // after teardown and fails the test run with "window is not defined".
  const pending = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(
    () => () => {
      if (pending.current) clearTimeout(pending.current);
    },
    []
  );

  /**
   * Re-renders the iframe after a brief delay (gives the backend time to persist). A second
   * save inside the delay restarts it, since one reload after the last save shows both.
   */
  const regeneratePreview = React.useCallback(() => {
    setRenderingPreview(true);
    if (pending.current) clearTimeout(pending.current);
    pending.current = setTimeout(() => {
      pending.current = null;
      setRenderingPreview(false);
      setRenderPreviewVersion((prev) => prev + 1);
    }, 1500);
  }, []);

  /** Bumps iframe cache-bust version (e.g. after inline body editor saves). */
  const bumpPreviewVersion = React.useCallback(() => {
    setRenderPreviewVersion((prev) => prev + 1);
  }, []);

  return {
    renderingPreview,
    renderPreviewVersion,
    regeneratePreview,
    bumpPreviewVersion
  };
}
